import { message } from "antd";
import { ContentTopic } from "api/config/chalice-types";
import { genericError } from "fixtures/globalConstants"; // Assuming genericError is correctly located
import { trackEvent } from "utils/eventTracking/trackEvents";

/**
 * Updates the business details with the new list of topics.
 * Handles dispatching the update, displaying success/warning messages,
 * tracking events, and managing errors.
 *
 * @param currentTopics The existing list of topics.
 * @param newTopics The newly generated topics to add.
 * @param dispatchUpdate The Redux dispatch function wrapped for business updates.
 * @param creationErrorsOccurred Flag indicating if errors happened during category creation.
 */
export const updateBusinessTopics = async (
  currentTopics: ContentTopic[],
  newTopics: ContentTopic[],
  // Consider using a more specific type for dispatchUpdate if available
  // e.g., (payload: { topics: ContentTopic[] }) => Promise<void>
  dispatchUpdate: (payload: { topics: ContentTopic[] }) => Promise<any>,
  creationErrorsOccurred: boolean
) => {
  // Combine existing and new topics
  const finalTopicsList = [...currentTopics, ...newTopics];

  if (newTopics.length === 0 && !creationErrorsOccurred) {
    // If no topics were added and no errors occurred, inform the user and exit.
    message.info(
      "No new topics were added (possibly duplicates or empty input)."
    );
    return; // Indicate successful completion without changes
  }

  try {
    // Dispatch the update action with the final list
    await dispatchUpdate({ topics: finalTopicsList });

    const numTopicsAdded = newTopics.length;
    if (numTopicsAdded > 0) {
      // Display success or warning message based on category creation errors
      if (!creationErrorsOccurred) {
        message.success(`${numTopicsAdded} new topic(s) added successfully.`);
      } else {
        message.warning(
          `Added ${numTopicsAdded} topic(s), but some category creation/mapping errors occurred.`
        );
      }
      // Track the successful update event
      trackEvent("updated_topics", { action: "add" });
    }
    // Note: If numTopicsAdded is 0 but creationErrorsOccurred is true,
    // a message was likely already shown during category creation.
    // We don't show an additional message here.
  } catch (error) {
    // Handle errors during the dispatch process
    message.error("Failed to update business details with new topics.");
    genericError(error); // Use the provided generic error handler
    // Re-throw the error to be caught by the calling hook
    throw error;
  }
};
